import ClientForm from "@components/ClientForm";
import CustomSnackBar from "@components/CustomSnackBar";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState,
  setSuccessAlert
} from "@constants/alerts";
import ROUTES from "@constants/routing";
import { DEFAULT_CLIENT } from "@context/Client/ClientContext";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Paper,
  SelectChangeEvent,
  Skeleton,
  Typography
} from "@mui/material";
import { useClient, usePatchClient, usePatchClientLogo } from "@services/api";
import {
  Client,
  ClientCreate,
  ClientUIMMMModelMetric,
  ClientUIModule
} from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { convertBase64ToFile } from "@utils/image";
import NavigationButtons from "@views/ViewAdminPanel/components/NavigationButtons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const ViewUpdateClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [client, setClient] = useState<Client | null>(null);
  const queryClient = useQueryClient();
  const [clientUpdating, setClientUpdating] = useState(false);

  const { id: clientId } = useParams<{ id: string }>();

  const { data, isFetching: isLoading, isError } = useClient(clientId || "");

  const mutationPatchClient = usePatchClient();
  const mutationPatchClientLogo = usePatchClientLogo();

  useEffect(() => {
    if (!isLoading && data === undefined) {
      setSnackbarAlert(
        setErrorAlert(t("patchClient.errorClientNotFound", { id: clientId }))
      );
    }
    if (!isLoading && !isError && data !== undefined) {
      setClient(data);
      // TODO: get filename with extension
      setSelectedFile(
        convertBase64ToFile(data.logo_path, `${data.name}_logo.jpg`)
      );
    }
  }, [data, isLoading, isError]);

  const onGoBack = () => {
    navigate(ROUTES.ADMIN_PANEL);
  };

  const handleFieldChange = (field: keyof ClientCreate, value: any) => {
    const value_fmt = `${value}`.length > 0 ? value : null;
    const updated = { ...client!!, [field]: value_fmt };
    setClient(updated);
  };

  const handleMMMModelMetricsChange = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;

    const selectedMetrics =
      typeof value === "string"
        ? (value.split(",") as ClientUIMMMModelMetric[])
        : (value as ClientUIMMMModelMetric[]);
    const updated = {
      ...client!!,
      mmm_model_config: {
        ...client!!.mmm_model_config,
        metrics: selectedMetrics
      }
    };
    setClient(updated as Client);
  };
  const handleMMMModelUIModulesChange = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;
    const selectedUIModules =
      typeof value === "string"
        ? (value.split(",") as ClientUIModule[])
        : (value as ClientUIModule[]);
    const updated = { ...client!!, ui_modules: selectedUIModules };
    setClient(updated as Client);
  };

  const onSave = (e: any) => {
    e.preventDefault();
    if (!e.target.checkValidity() || selectedFile === null) {
      setSnackbarAlert(setErrorAlert(t("newClient.validation.formInvalid")));
    } else {
      setClientUpdating(true);
      mutationPatchClient.mutate(
        {
          client_id: clientId || "",
          updates: client!!
        },
        {
          onSuccess: () => {
            mutationPatchClientLogo.mutate(
              {
                client_id: clientId || "",
                logo: selectedFile
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["clients"]
                  });
                  queryClient.refetchQueries({
                    queryKey: ["clients"]
                  });

                  queryClient.invalidateQueries({
                    queryKey: ["client", clientId || ""]
                  });
                  queryClient.refetchQueries({
                    queryKey: ["client", clientId || ""]
                  });
                  setClientUpdating(false);
                  setSnackbarAlert(setSuccessAlert(t("patchClient.success")));
                },
                onError: () => {
                  setClientUpdating(false);
                  setSnackbarAlert(
                    setErrorAlert(t("newClient.validation.errorSavingLogo"))
                  );
                }
              }
            );
          },
          onError: () => {
            setClientUpdating(false);
            setSnackbarAlert(setErrorAlert(t("patchClient.error_metadata")));
          }
        }
      );
    }
  };

  return (
    <Box className="w-full p-4">
      <NavigationButtons disabled={isLoading || clientUpdating} />
      <Box className="flex justify-center w-full p-2 md:p-8 mt-8">
        <Paper className="w-full">
          <Box className="flex items-center justify-center pt-16 gap-2">
            <EditIcon color="info" sx={{ fontSize: "2.0rem" }} />
            <Typography variant="h6">{t("common.editClient")}</Typography>
          </Box>
          {isLoading || client === null ? (
            <Box className="p-8">
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
              </Box>
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
              </Box>
              <Box className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
              </Box>
            </Box>
          ) : (
            <ClientForm
              mode="update"
              client={client || DEFAULT_CLIENT.client}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              processing={clientUpdating}
              onSave={onSave}
              onGoBack={onGoBack}
              handleFieldChange={handleFieldChange}
              handleMMMModelMetricsChange={handleMMMModelMetricsChange}
              handleMMMModelUIModulesChange={handleMMMModelUIModulesChange}
            />
          )}
        </Paper>
        <CustomSnackBar
          id={snackbarAlert.message}
          key={snackbarAlert.message}
          open={snackbarAlert.open}
          message={snackbarAlert.message}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          autoHideDuration={snackbarAlert.autoHideDuration}
          color={snackbarAlert.severity}
        />
      </Box>
    </Box>
  );
};

export default ViewUpdateClient;
